/**
 * Root Route Component
 * Wraps all routes with common layout and providers
 */

import {
  createRootRoute,
  Outlet,
  useLocation,
  useRouterState,
} from "@tanstack/react-router";
import { Toaster } from "react-hot-toast";
import React from "react";
import { NotFoundPage } from "../pages/NotFound/NotFoundPage";
import { Loading } from "../components/common/Loading/Loading";
import { ErrorBoundary } from "../components/ErrorBoundary";
import { useAnnouncement } from "../hooks/useAnnouncement";
import { AnnouncementBanner } from "../components/announcement/AnnouncementBanner";
import { AnnouncementPage } from "../components/announcement/AnnouncementPage";
import { useAppUpdate } from "../hooks/useAppUpdate";
import { UpdateBanner } from "../components/update-notifier/UpdateBanner";
import { useSpendCap } from "../hooks/useSpendCap";
import { SpendCapBanner } from "../components/announcement/SpendCapBanner";
import { ConfirmEmailBanner } from "../components/announcement/ConfirmEmailBanner";

// Root component that wraps all routes
const RootComponent: React.FC = () => {
  const location = useLocation();
  const router = useRouterState();
  const { active: activeAnnouncement, dismiss: dismissAnnouncement } = useAnnouncement();
  const { spendCap } = useSpendCap();
  const { updateAvailable } = useAppUpdate();
  const isNotFound = location.pathname === "/not-found";
  const isLoading = location.pathname === "/loading";

  // Blocking page announcements cover the entire app, including unauth routes.
  if (activeAnnouncement?.mode === "page") {
    return (
      <ErrorBoundary>
        <AnnouncementPage active={activeAnnouncement} />
      </ErrorBoundary>
    );
  }

  // Check if router is loading (during route guard execution, etc.)
  // This prevents showing NotFoundPage when routes are still being matched/loaded
  const isRouterLoading = router.status === "pending" || router.isLoading;

  if (isNotFound) {
    return <NotFoundPage />;
  }
  if (isLoading) {
    return <Loading />;
  }

  // Show loading state when router is loading (e.g., during beforeLoad guard execution)
  // This fixes the issue where refreshing shows NotFoundPage during route guard execution
  if (isRouterLoading && !router.location.href) {
    // Only show full loader on initial load (when history is empty-ish)
    // Checking router.location might help distinguish soft vs hard nav
    return (
      <ErrorBoundary>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: "#363636",
              color: "#fff",
            },
          }}
        />
        <Loading />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      {/* Toast notifications provider */}
      <Toaster
        position="top-right"
        toastOptions={{
          // Default options for all toasts
          duration: 4000,
          style: {
            background: "#363636",
            color: "#fff",
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: "#10b981",
              secondary: "#fff",
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: "#ef4444",
              secondary: "#fff",
            },
          },
        }}
      />

      {/* New-version-available banner (non-blocking, persistent until refresh) */}
      {updateAvailable && <UpdateBanner />}

      {/* System spend-cap banner. Above the admin announcement because it is
          the reason the user cannot record RIGHT NOW, it should be the first
          thing they see. Automatic: no Announcement row backs it, and it
          clears itself when the cap resets or an admin raises it. */}
      {spendCap?.exceeded && <SpendCapBanner spendCap={spendCap} />}

      {/* Reminder to confirm the email; the emailed link's own page speaks for itself. */}
      {location.pathname !== "/verify-email" && <ConfirmEmailBanner />}

      {/* Top-of-app announcement banner (non-blocking) */}
      {activeAnnouncement?.mode === "banner" && (
        <AnnouncementBanner
          active={activeAnnouncement}
          onDismiss={() => dismissAnnouncement(activeAnnouncement)}
        />
      )}

      {/* Outlet renders the matched child route */}
      <Outlet />

      {/* TanStack Router Devtools (only in development) */}
      {import.meta.env.DEV && (
        <React.Suspense fallback={null}>
          <div
            style={{
              position: "fixed",
              bottom: "1rem",
              left: "1rem",
              zIndex: 99999,
            }}
          >
            {/* Router devtools will be lazy loaded */}
          </div>
        </React.Suspense>
      )}
    </ErrorBoundary>
  );
};

// Create root route (note: in TanStack Router v1, createRootRoute takes no arguments)
// The component is exported separately and will be picked up by the route tree
export const rootRoute = createRootRoute();

// Override the component separately
rootRoute.update({
  component: RootComponent,
});

// Export as Route for route tree compatibility
export const Route = rootRoute;
